import React, { useEffect, useState } from 'react';
import { Heading1 } from './Heading1';
import { Heading2 } from './Heading2';
import { Heading3 } from './Heading3';
import { Paragraph } from './Paragraph';
import { BulletList } from './BulletList';
import { CodeBlock } from './CodeBlock';
import { ImageWithCaption } from './ImageWithCaption';
import { BlogImage } from './BlogImage';
import { Quote } from './Quote';
import { Callout } from './Callout';
import { ComparisonTable } from './ComparisonTable';
import { Divider } from './Divider';
import { FeatureList } from './FeatureList';
import { ProcessStep } from './ProcessStep';
import { TwoColumnProblemOverview } from './TwoColumnProblemOverview';
import { MarkdownContent } from './MarkdownContent';
import { useLightboxStyles } from './useLightboxStyles';
import { KeyBulletList } from './KeyBulletList';
import { ImpactStatement } from './ImpactStatement';
import { StyledCallout } from './StyledCallout';
import { BlogGallery } from './BlogGallery';
import { MermaidGallery } from './MermaidGallery';

type BlogContentRendererVariant = 'default' | 'caseStudy';

// Blog Content Renderer
export const BlogContentRenderer: React.FC<{
  content: any[];
  postTitle: string;
  variant?: BlogContentRendererVariant;
}> = ({ content, postTitle, variant = 'default' }) => {
  // Apply lightbox styles
  useLightboxStyles();

  // State for tracking gallery groups
  const [galleryGroups, setGalleryGroups] = useState<Record<string, any[]>>({});

  // Process content to identify gallery groups
  useEffect(() => {
    const groups: Record<string, any[]> = {};

    content.forEach((block) => {
      if (block.type === 'image' && block.content.galleryGroup) {
        const groupName = block.content.galleryGroup;
        if (!groups[groupName]) {
          groups[groupName] = [];
        }

        groups[groupName].push({
          src: block.content.src,
          alt: block.content.alt,
          caption: block.content.caption,
        });
      }
    });

    setGalleryGroups(groups);
  }, [content]);

  const isMediaBlock = (type: string) =>
    type === 'image' || type === 'imageWithCaption' || type === 'gallery' || type === 'mermaidGallery';
  const containerClass =
    variant === 'caseStudy'
      ? 'case-study-content w-full space-y-0'
      : 'px-4 sm:px-6 md:px-8 lg:px-0';

  // Skip the first block if it's a heading1 (we'll render it separately)
  const startIndex = content[0]?.type === 'heading1' ? 1 : 0;

  return (
    <div className={containerClass}>
      {/* Render the first heading separately if it exists */}
      {content[0]?.type === 'heading1' && (
        <Heading1 id="post-title" description={content[0].description}>
          {postTitle}
        </Heading1>
      )}

      {/* Render the rest of the content */}
      {content.slice(startIndex).map((block, index) => {
        let renderedBlock: React.ReactNode = null;

        switch (block.type) {
          case 'heading1':
            renderedBlock = (
              <Heading1 id={block.id} description={block.description}>
                {block.content}
              </Heading1>
            );
            break;
          case 'heading2':
            renderedBlock = (
              <Heading2 id={block.id} description={block.description}>
                {block.content}
              </Heading2>
            );
            break;
          case 'heading3':
            renderedBlock = (
              <Heading3 id={block.id} description={block.description}>
                {block.content}
              </Heading3>
            );
            break;
          case 'paragraph':
            renderedBlock = <Paragraph>{block.content}</Paragraph>;
            break;
          case 'bulletList':
            renderedBlock = <BulletList items={block.content} />;
            break;
          case 'codeBlock':
            renderedBlock = (
              <CodeBlock
                code={block.content.code}
                language={block.content.language}
              />
            );
            break;
          case 'imageWithCaption':
            renderedBlock = (
              <ImageWithCaption
                src={block.content.src}
                alt={block.content.alt}
                caption={block.content.caption}
              />
            );
            break;
          case 'quote':
            renderedBlock = <Quote>{block.content}</Quote>;
            break;
          case 'callout':
            renderedBlock = <Callout>{block.content}</Callout>;
            break;
          case 'comparisonTable':
            renderedBlock = (
              <ComparisonTable
                headers={block.content.headers}
                rows={block.content.rows}
              />
            );
            break;
          case 'divider':
            renderedBlock = <Divider />;
            break;
          case 'featureList':
            renderedBlock = <FeatureList items={block.content} />;
            break;
          case 'processStep':
            renderedBlock = (
              <ProcessStep
                number={block.content.number}
                title={block.content.title}
                description={block.content.description}
              />
            );
            break;
          case 'twoColumnProblemOverview':
            renderedBlock = (
              <TwoColumnProblemOverview
                problem={block.content.problem}
                background={block.content.background}
              />
            );
            break;
          case 'markdown':
            renderedBlock = <MarkdownContent content={block.content} />;
            break;
          case 'image':
            renderedBlock = (
              <BlogImage content={block.content} galleryGroups={galleryGroups} />
            );
            break;
          case 'keyBulletList':
            renderedBlock = (
              <KeyBulletList
                heading={block.content.heading}
                description={block.content.description}
                points={block.content.points}
              />
            );
            break;
          case 'impactStatement':
            renderedBlock = <ImpactStatement content={block.content} />;
            break;
          case 'gallery':
            renderedBlock = (
              <BlogGallery
                heading={block.content.heading}
                caption={block.content.caption}
                rows={block.content.rows}
                shapedImages={variant !== 'caseStudy'}
              />
            );
            break;
          case 'styledCallout':
            renderedBlock = (
              <StyledCallout
                accentColor={block.content.accentColor}
                accentText={block.content.accentText}
                lines={block.content.lines}
                text={block.content.text}
              >
                {block.content.jsx && (
                  <div
                    dangerouslySetInnerHTML={{ __html: block.content.jsx }}
                  />
                )}
              </StyledCallout>
            );
            break;
          case 'mermaidGallery':
            renderedBlock = (
              <MermaidGallery
                heading={block.content.heading}
                caption={block.content.caption}
                rows={block.content.rows}
              />
            );
            break;
          default:
            console.warn(`Unknown block type: ${block.type}`);
        }

        if (!renderedBlock) return null;

        if (variant === 'caseStudy') {
          const media = isMediaBlock(block.type);
          const wrapperClasses = media
            ? 'case-study-block case-study-block--media w-full my-48 px-6 sm:px-16 lg:px-32'
            : 'case-study-block case-study-block--text mx-auto my-10 w-full max-w-4xl px-6 sm:px-8';

          return (
            <div key={index} className={wrapperClasses}>
              {renderedBlock}
            </div>
          );
        }

        return <React.Fragment key={index}>{renderedBlock}</React.Fragment>;
      })}
    </div>
  );
};
